import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ── Data-driven additive knowledge (mirrors recipe_knowledge.py) ─────────────

const UNIVERSAL_ADDITIVES = new Set(['ADD_CDF-6P']);

const RETARDANT_COLS = new Set(['ADD_LTR', 'ADD_MCR-7', 'ADD_HTR-2', 'ADD_CFR-5', 'ADD_CFR-2', 'ADD_LCF-7']);

// Ordered by historical frequency / importance — used for smart slider defaults
const SMART_ADDITIVE_ORDER = [
  'ADD_CDF-6P',   // 100% — universal, always first
  'ADD_MCR-7',    // 63.9% — primary cool-well retardant
  'ADD_SCA-7',    // 69.5% — fluid loss
  'ADD_ASM-3',    // 56.8% — dispersant (bundles with MCR-7 + SCA-7)
  'ADD_CFL-4',    // 57.2% — friction reducer (bundles with HTR-2)
  'ADD_ASM-8',    // 43.1% — anti-settling
  'ADD_SCA-6',    // 42.3% — fluid loss (bundles with MCR-7)
  'ADD_FWC-2',    // 32.8% — free water control (bundles with ASM-3)
  'ADD_HTR-2',    // 28.8% — high-temp retardant
  'ADD_CFL-3',    // 17.6%
  'ADD_CFR-2',    // 16.4% — retardant
  'ADD_CFL-12',   // 12.8%
  'ADD_EA-5',     // 12.7%
  'ADD_CFR-5',    // 10.5% — retardant
  'ADD_GCA-2',    //  6.9%
  'ADD_LTR',      //  5.5% — liquid retardant
  'ADD_LCC-1',    //  5.3%
  'ADD_LCF-7',    //  4.5% — retardant
  'ADD_D270',     //  3.1%
  'ADD_WIE-2',    //  2.8%
  'ADD_EA-1',     //  0.9%
  'ADD_GSS-1',    //  0.1%
];

// Default selection: Template-A core combo (covers 9.4% of all historical experiments)
// CDF-6P (universal) + MCR-7 (retardant) + SCA-7 + ASM-3
const DEFAULT_ADDITIVES_COOL = ['ADD_CDF-6P', 'ADD_MCR-7', 'ADD_SCA-7', 'ADD_ASM-3'];
const DEFAULT_ADDITIVES_HOT = ['ADD_CDF-6P', 'ADD_HTR-2', 'ADD_CFL-4', 'ADD_ASM-3'];

function getDefaultAdditives(bhct) {
  return bhct != null && parseFloat(bhct) >= 80
    ? DEFAULT_ADDITIVES_HOT
    : DEFAULT_ADDITIVES_COOL;
}

function getAdditiveCategory(name) {
  if (UNIVERSAL_ADDITIVES.has(name)) return 'universal';
  if (RETARDANT_COLS.has(name)) return 'retardant';
  return 'standard';
}

const CATEGORY_LABELS = {
  universal: { label: '∞ Universal', color: '#7c3aed' },
  retardant: { label: '⏱ Retardant', color: '#b45309' },
  standard: { label: null, color: null },
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RecipeGenerator = () => {
  const [schema, setSchema] = useState(null);
  const [targetTT, setTargetTT] = useState({
    TT40_min: '',
    TT70_min: '',
    TT100_min: ''
  });
  const [fixedFeatures, setFixedFeatures] = useState({});
  const [featureBounds, setFeatureBounds] = useState({});
  const [activeAdditives, setActiveAdditives] = useState(DEFAULT_ADDITIVES_COOL);
  const [nRecipes, setNRecipes] = useState(3);
  const [tolerance, setTolerance] = useState(60.0);
  const [maxAdditives, setMaxAdditives] = useState(DEFAULT_ADDITIVES_COOL.length);
  const [recipes, setRecipes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(true);

  // Load schema on mount
  useEffect(() => {
    axios.get(`${API_URL}/schema`)
      .then(response => {
        setSchema(response.data);
        // Smart default: Template-A core (best starting combo from historical data)
        setActiveAdditives(DEFAULT_ADDITIVES_COOL);
        setMaxAdditives(DEFAULT_ADDITIVES_COOL.length);
      })
      .catch(err => {
        setError('Failed to load schema. Is the API running?');
      });
  }, []);

  // When BHCT is fixed, swap the default retardant (MCR-7 ↔ HTR-2)
  useEffect(() => {
    const bhct = fixedFeatures['BHCT'];
    if (bhct === '' || bhct === undefined) return;
    setActiveAdditives(prev => {
      const newDefaults = getDefaultAdditives(bhct);
      // Only swap if the user hasn't customised away from a pure default set
      const isDefaultCool = DEFAULT_ADDITIVES_COOL.every(a => prev.includes(a)) && prev.length === DEFAULT_ADDITIVES_COOL.length;
      const isDefaultHot = DEFAULT_ADDITIVES_HOT.every(a => prev.includes(a)) && prev.length === DEFAULT_ADDITIVES_HOT.length;
      if (!isDefaultCool && !isDefaultHot) return prev; // user customised — don't override
      return newDefaults;
    });
  }, [fixedFeatures['BHCT']]);

  const handleTargetChange = (target, value) => {
    setTargetTT(prev => ({
      ...prev,
      [target]: value
    }));
  };

  const handleFixedFeatureToggle = (feature) => {
    setFixedFeatures(prev => {
      const newFixed = { ...prev };
      if (newFixed[feature] !== undefined) {
        delete newFixed[feature];
      } else {
        newFixed[feature] = '';
      }
      return newFixed;
    });
  };

  const handleFixedFeatureChange = (feature, value) => {
    setFixedFeatures(prev => ({
      ...prev,
      [feature]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build target_tt object (only include non-empty values)
      const processedTargets = {};
      Object.keys(targetTT).forEach(key => {
        if (targetTT[key] !== '') {
          processedTargets[key] = parseFloat(targetTT[key]);
        }
      });

      if (Object.keys(processedTargets).length === 0) {
        throw new Error('Please specify at least one target TT value');
      }

      // Process fixed features
      const processedFixed = {};
      Object.keys(fixedFeatures).forEach(key => {
        if (fixedFeatures[key] !== '') {
          const featureType = schema.feature_types[key];
          if (featureType === 'numeric') {
            processedFixed[key] = parseFloat(fixedFeatures[key]);
          } else {
            processedFixed[key] = fixedFeatures[key];
          }
        }
      });

      const requestData = {
        target_tt: processedTargets,
        fixed_features: processedFixed,
        active_additives: activeAdditives.length > 0 ? activeAdditives : null,  // NEW
        n_recipes: nRecipes,
        tolerance: tolerance,
        use_smart_init: true  // NEW: enable smart initialization
      };

      // Add timeout (2 minutes) for potentially long-running optimization
      const response = await axios.post(`${API_URL}/generate-recipe`, requestData, {
        timeout: 120000  // 2 minutes timeout
      });
      setRecipes(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Recipe generation failed');
    } finally {
      setLoading(false);
    }
  };

  if (!schema) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading schema...</p>
      </div>
    );
  }

  return (
    <div className="recipe-generator-container">
      <div className="intro-section">
        <h2>Inverse Prediction - Recipe Generation</h2>
        <p>
          Specify your target thickening times and constraints, and we'll generate cement recipes
          that are likely to achieve those targets.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="recipe-form">
        {/* Target TT Section */}
        <div className="form-section">
          <h3>Target Thickening Times</h3>
          <p className="section-help">Specify desired TT values (leave empty if not important)</p>

          <div className="targets-grid">
            <div className="form-group">
              <label htmlFor="TT40_min">
                TT40 (minutes)
                <span className="optional-badge">optional</span>
              </label>
              <input
                type="number"
                id="TT40_min"
                value={targetTT.TT40_min}
                onChange={(e) => handleTargetChange('TT40_min', e.target.value)}
                step="any"
                placeholder="e.g., 35"
              />
            </div>

            <div className="form-group">
              <label htmlFor="TT70_min">
                TT70 (minutes)
                <span className="optional-badge">optional</span>
              </label>
              <input
                type="number"
                id="TT70_min"
                value={targetTT.TT70_min}
                onChange={(e) => handleTargetChange('TT70_min', e.target.value)}
                step="any"
                placeholder="e.g., 50"
              />
            </div>

            <div className="form-group">
              <label htmlFor="TT100_min">
                TT100 (minutes)
                <span className="optional-badge">optional</span>
              </label>
              <input
                type="number"
                id="TT100_min"
                value={targetTT.TT100_min}
                onChange={(e) => handleTargetChange('TT100_min', e.target.value)}
                step="any"
                placeholder="e.g., 75"
              />
            </div>
          </div>
        </div>

        {/* Fixed Features Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>🔒 Fixed Constraints</h3>
            <button
              type="button"
              className="toggle-button"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? '▼ Hide' : '▶ Show'} Advanced Options
            </button>
          </div>

          {showAdvanced && (
            <>
              <p className="section-help">
                Select features that must remain at specific values (e.g., temperature, pressure)
              </p>

              <div className="features-grid">
                {schema.required_features
                  .filter(f => !f.startsWith('ADD_'))   // additives live in the section below
                  .map(feature => {
                    const range = schema.numeric_ranges?.[feature];
                    return (
                      <div key={feature} className="form-group">
                        <label className="checkbox-label" style={{ marginBottom: '0.4rem' }}>
                          <input
                            type="checkbox"
                            checked={fixedFeatures[feature] !== undefined}
                            onChange={() => handleFixedFeatureToggle(feature)}
                          />
                          <span style={{ fontWeight: 500 }}>{feature}</span>
                        </label>

                        {fixedFeatures[feature] !== undefined && (
                          schema.feature_types[feature] === 'categorical' ? (
                            <select
                              value={fixedFeatures[feature]}
                              onChange={(e) => handleFixedFeatureChange(feature, e.target.value)}
                              className="inline-select"
                              style={{ width: '100%' }}
                            >
                              <option value="">Select...</option>
                              {schema.categorical_values[feature]?.map(val => (
                                <option key={val} value={val}>{val}</option>
                              ))}
                            </select>
                          ) : (
                            <>
                              <input
                                type="number"
                                value={fixedFeatures[feature]}
                                onChange={(e) => handleFixedFeatureChange(feature, e.target.value)}
                                step="any"
                                placeholder={range ? `${range.min} – ${range.max}` : 'Enter value'}
                                min={range?.min}
                                max={range?.max}
                                className="inline-input"
                                style={{ width: '100%' }}
                              />
                              {range && (
                                <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', display: 'block' }}>
                                  Historical range: {range.min} – {range.max}
                                </span>
                              )}
                            </>
                          )
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>

        {/* Active Additives Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>Active Additives</h3>
            <p className="section-help">
              Defaults are the most common historical combo. Fewer additives = faster optimization.
              <br />
              <span style={{ color: '#7c3aed', fontWeight: 500 }}>∞ Universal</span> additives (ADD_CDF-6P) are always included.
              &nbsp;Changing BHCT above will auto-swap the retardant.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="maxAdditives">
              Max Active Additives: <strong>{maxAdditives}</strong>
              <span className="speed-indicator">
                {maxAdditives <= 3 && 'Very Fast'}
                {maxAdditives > 3 && maxAdditives <= 5 && 'Fast'}
                {maxAdditives > 5 && maxAdditives <= 8 && 'Medium'}
                {maxAdditives > 8 && 'Slow'}
              </span>
            </label>
            <input
              type="range"
              id="maxAdditives"
              min="1"
              max="22"
              step="1"
              value={maxAdditives}
              onChange={(e) => {
                const newMax = parseInt(e.target.value);
                setMaxAdditives(newMax);
                // Select top-N by historical importance, not raw schema order
                const schemaAdditives = new Set(schema.required_features.filter(f => f.startsWith('ADD_')));
                const ordered = SMART_ADDITIVE_ORDER.filter(a => schemaAdditives.has(a));
                setActiveAdditives(ordered.slice(0, newMax));
              }}
            />
            <div className="range-labels">
              <span>1 (fastest)</span>
              <span>22 (all)</span>
            </div>
          </div>

          <div className="additive-selection">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <p style={{ margin: 0 }}><strong>Selected Additives ({activeAdditives.length}):</strong></p>
              <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem' }}>
                <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>∞ Universal</span>
                <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>⏱ Retardant</span>
              </div>
            </div>
            <div className="additive-pills">
              {activeAdditives.length === 0 ? (
                <span className="info-text">All 22 additives will be optimized (slower)</span>
              ) : (
                activeAdditives.map(additive => {
                  const cat = getAdditiveCategory(additive);
                  const pillStyle = cat === 'universal'
                    ? { background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }
                    : cat === 'retardant'
                      ? { background: 'linear-gradient(135deg, #d97706, #b45309)' }
                      : {};
                  return (
                    <span key={additive} className="additive-pill" style={pillStyle}>
                      {additive}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveAdditives(prev => prev.filter(a => a !== additive));
                          setMaxAdditives(prev => Math.max(1, prev - 1));
                        }}
                        className="remove-pill"
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            <details className="additive-selector">
              <summary>▼ Customize additive selection</summary>
              <div style={{ padding: '0.75rem 0 0.25rem', fontSize: '0.75rem', color: '#64748b' }}>
                Additives are sorted by historical usage frequency. Selecting fewer additives is faster.
              </div>
              <div className="additive-grid">
                {/* Render in smart order so most important are at the top */}
                {(() => {
                  const schemaSet = new Set(schema.required_features.filter(f => f.startsWith('ADD_')));
                  const ordered = [
                    ...SMART_ADDITIVE_ORDER.filter(a => schemaSet.has(a)),
                    ...[...schemaSet].filter(a => !SMART_ADDITIVE_ORDER.includes(a)),
                  ];
                  return ordered.map(additive => {
                    const cat = getAdditiveCategory(additive);
                    const { label: catLabel, color: catColor } = CATEGORY_LABELS[cat];
                    return (
                      <label key={additive} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={activeAdditives.includes(additive)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setActiveAdditives(prev => [...prev, additive]);
                              setMaxAdditives(prev => prev + 1);
                            } else {
                              setActiveAdditives(prev => prev.filter(a => a !== additive));
                              setMaxAdditives(prev => Math.max(1, prev - 1));
                            }
                          }}
                        />
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1 }}>
                          {additive}
                          {catLabel && (
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 600,
                              color: catColor,
                              background: cat === 'universal' ? '#ede9fe' : '#fef3c7',
                              padding: '1px 5px', borderRadius: '8px',
                              whiteSpace: 'nowrap',
                            }}>
                              {catLabel}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  });
                })()}
              </div>
            </details>
          </div>
        </div>

        {/* Generation Settings */}
        <div className="form-section">
          <h3>Generation Settings</h3>

          <div className="settings-grid">
            <div className="form-group">
              <label htmlFor="nRecipes">
                Number of Recipes: <strong>{nRecipes}</strong>
              </label>
              <input
                type="range"
                id="nRecipes"
                min="3"
                max="10"
                step="1"
                value={nRecipes}
                onChange={(e) => setNRecipes(parseInt(e.target.value))}
              />
              <div className="range-labels">
                <span>3</span>
                <span>10</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tolerance">
                Tolerance (±minutes): <strong>{tolerance.toFixed(1)}</strong>
              </label>
              <input
                type="range"
                id="tolerance"
                min="1"
                max="120"
                step="1"
                value={tolerance}
                onChange={(e) => setTolerance(parseFloat(e.target.value))}
              />
              <div className="range-labels">
                <span>1 min</span>
                <span>120 min</span>
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Generating Recipes...' : 'Generate Recipes'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {recipes && (
        <div className="results-section">
          <h2>🎉 Generated Recipes</h2>

          <div className="summary-card">
            <h4>Summary</h4>
            <div className="summary-grid">
              <div><strong>Total Generated:</strong> {recipes.summary.total_generated}</div>
              <div><strong>Meeting Tolerance:</strong> {recipes.summary.meeting_tolerance}</div>
              <div><strong>Best Error:</strong> {recipes.summary.best_error?.toFixed(2)} minutes</div>
              <div><strong>Target TT:</strong> {Object.entries(recipes.summary.target_tt).map(([k, v]) => `${k.replace('_min', '')}=${v}`).join(', ')}</div>
            </div>
          </div>

          <div className="recipes-list">
            {recipes.recipes.map((recipe, idx) => (
              <div key={idx} className={`recipe-card ${recipe.meets_target ? 'meets-target' : ''}`}>
                <div className="recipe-header">
                  <h3>Recipe #{idx + 1}</h3>
                  <div className="recipe-badges">
                    {recipe.meets_target && <span className="badge success">✓ Meets Target</span>}
                    {recipe.optimization_success && <span className="badge info">Optimized</span>}
                  </div>
                </div>

                <div className="recipe-metrics">
                  <div className="metric">
                    <span className="metric-label">Error (RMSE):</span>
                    <span className="metric-value">{recipe.error.toFixed(2)} min</span>
                  </div>
                </div>

                <div className="predicted-tt-section">
                  <h4>Predicted Thickening Times (90% Confidence Interval)</h4>
                  <div className="predicted-tt-grid">
                    {Object.entries(recipe.predicted_tt).map(([target, value]) => {
                      const targetValue = recipes.summary.target_tt[target];
                      const diff = targetValue ? value - targetValue : null;
                      const ci = recipe.predicted_tt_ci?.[target];

                      return (
                        <div key={target} className="predicted-tt-item">
                          <div className="tt-name">{target.replace('_min', '')}</div>
                          <div className="tt-value">{value.toFixed(1)} min</div>
                          {ci && (
                            <div className="tt-ci">
                              [{ci.lower_ci.toFixed(1)}, {ci.upper_ci.toFixed(1)}]
                            </div>
                          )}
                          {diff !== null && (
                            <div className={`tt-diff ${diff > 0 ? 'positive' : 'negative'}`}>
                              {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="recipe-formulation">
                  <h4>Formulation</h4>
                  <button
                    type="button"
                    className="expand-button"
                    onClick={(e) => {
                      const content = e.target.nextElementSibling;
                      content.style.display = content.style.display === 'none' ? 'grid' : 'none';
                      e.target.textContent = content.style.display === 'none' ? '▶ Show Details' : '▼ Hide Details';
                    }}
                  >
                    ▶ Show Details
                  </button>
                  <div className="formulation-grid" style={{ display: 'none' }}>
                    {Object.entries(recipe.recipe)
                      .filter(([feature, value]) => {
                        // Hide ADD_ features with value 0
                        if (feature.startsWith('ADD_') && value === 0) {
                          return false;
                        }
                        return true;
                      })
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([feature, value]) => (
                        <div key={feature} className="formulation-item">
                          <span className="feature-name">{feature}:</span>
                          <span className="feature-value">
                            {typeof value === 'number' ? value.toFixed(3) : value}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="warning-box">
            <h4>Important Notes</h4>
            <ul>
              <li><strong>Validate in Lab:</strong> Always test generated recipes in laboratory conditions before field use</li>
              <li><strong>Check Reasonability:</strong> Review additive amounts and chemical compatibility</li>
              <li><strong>Model Limitations:</strong> Predictions have uncertainty - use confidence intervals</li>
              <li><strong>Safety First:</strong> Verify all safety requirements and regulations</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeGenerator;
